/// <amd-module name="Release/DeploymentsSection/Timeline" />
import * as React from "react";

import {
    Components,
    IVssLayoutManager,
    IVssComponentContext,
    VssComponent
} from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssPerformanceService } from "VSS/Platform/Performance";

import { VssIconType } from "VSSUI/VssIcon";

import { DeploymentsComponent, DeploymentsSummaryPerfConstants } from "Release/DeploymentsSection/DeploymentComponent";
import { DeploymentsComponentStore } from "Release/DeploymentsSection/Stores/DeploymentsComponentStore";
import { DeploymentsComponentActionsCreator } from "Release/DeploymentsSection/Actions/DeploymentsComponentActionsCreator";
import { DeploymentsComponentActions } from "Release/DeploymentsSection/Actions/DeploymentsComponentActions";
import { DeploymentsSignalRHub } from "Release/DeploymentsSection/SignalRHub";
import { SignalRStore } from "Release/DeploymentsSection/Stores/SignalR";
import { DeploymentsSignalRActionCreator, DeploymentsSignalRActionHub } from "Release/DeploymentsSection/Actions/SignalR";
import { DeploymentsSource } from "DeploymentsSource";

interface ISnapshotSampleProps extends IContributedTimelineSnapshotProps {

}

interface ISnapshotSampleState {
    deploymentsCount: number;
}

export class SnapshotSample extends VssComponent<ISnapshotSampleProps, ISnapshotSampleState> {
    private _actionsCreator: DeploymentsComponentActionsCreator;
    private _store: DeploymentsComponentStore;
    private _signalRHub: DeploymentsSignalRHub;
    private _signalRStore: SignalRStore;
    private _deploymentsSignalRActionHub: DeploymentsSignalRActionHub;
    private _deploymentsSignalRActionCreator: DeploymentsSignalRActionCreator;

    constructor(props: ISnapshotSampleProps, context: IVssComponentContext) {
        super(props, context);
        this._initializeFlux();
    }

    public render() {
        let deploymentComponentProps = {
            store: this._store,
            actionsCreator: this._actionsCreator,
            signalRHub: this._signalRHub
        };

        return <SnapshotSampleHolder
            className="build-sample-snapshot"
            snapshotKey={this.props.snapshotKey}
            iconProps={
                {
                    name: "Rocket",
                    type: VssIconType.fabric
                }
            }
            statusIconProps={
                this.state.deploymentsCount
            }
            initializeSnapshot={this._initializeSnapshot}
            ref={this.props.snapshotRef}>
            <DeploymentsComponent {...deploymentComponentProps}/>
        </SnapshotSampleHolder>;
    }

    public componentDidMount() {
        this._store.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
        this._store.dispose();
    }

    private _onStoreChanged = (): void => {
        let state = this._store.getState();
		let totalDeploymentsCount = state.succeededDeploymentsCount + state.inProgressDeploymentsCount + state.failedDeploymentsCount + state.partiallySucceededCount;
        this.setState({
            deploymentsCount: totalDeploymentsCount
        });
    }

    private _initializeSnapshot = (build: any, callback: (marker: Date) => void) => {
        this._initializeSignalRHub(build);
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario(DeploymentsSummaryPerfConstants.Scenario, false);

        this._actionsCreator.initializeData(build).then(() => {
            callback(new Date());
        });
    }

    private _initializeFlux(): void {
        this._deploymentsSignalRActionHub = new DeploymentsSignalRActionHub();
        this._deploymentsSignalRActionCreator = new DeploymentsSignalRActionCreator({
            actionHub: this._deploymentsSignalRActionHub
        });
        this._signalRStore = new SignalRStore({
            actionCreator: this._deploymentsSignalRActionCreator
        });
        const actions = new DeploymentsComponentActions();
        this._store = new DeploymentsComponentStore(actions, this._signalRStore);
        let source = new DeploymentsSource(this.context.pageContext);
        this._actionsCreator = new DeploymentsComponentActionsCreator(actions, source);
        this.state = {
            deploymentsCount: 0
        };
    }

    private _initializeSignalRHub(build: any) {
        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        let contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        let data = contributionService.getData<any>("ms.vss-releaseManagement-web.rm-signalr-data-provider-new");

        const vssPageData = vssPageService.getData();
        if (vssPageData && data && data.connectionUrl) {
            const hostId = vssPageData.hostId;
            const isHosted = vssPageData.isHosted;
            
            this._signalRHub = new DeploymentsSignalRHub({
                actionCreator: this._deploymentsSignalRActionCreator,
                hostInformation: {
                    hostId: hostId,
                    isHosted: isHosted,
                    hostPath: data.connectionUrl.split("_apis")[0],
                    projectId: build.project.id
                },
                projectId: build.project.id
            });
        }
    }
}

// By doing this, we can avoid dependency on the Timeline component code it'self
// Otherwise, if it's already pulled in, then we could use TimelineSnapshot component
class SnapshotSampleHolder extends VssComponent<ITimelineSnapshotProps, {}> {
	public render() {
		return <div>Note! This is just a holder, this text would never show up!</div>;
	}
}

// Once platform checkin to allow csproj to reference webproj is done, need remove this duplicate code.
export interface IContributedTimelineSnapshotProps extends ITimelineSnapshotWrapperProps {
    /**
     * Ref of the component that takes ITimelineSnapshotProps
     */
	snapshotRef: React.Ref<any>;
}

export interface ITimelineSnapshotWrapperProps {
    /**
     * Unique key given for this snapshot.
     * This must be supplied, snapshot will not be rendered with out the key.
     */
	snapshotKey: string;
}

export interface ITimelineSnapshotProps extends ITimelineSnapshotWrapperProps {
    /**
     * Icon class name to be displayed to the left of the snapshot.
     * Ensure that css responsible for loading the icon is loaded with the component.
     */
	iconProps: ITimelineIconProps;

	/**
     * If set, this will render status icon on the timeline, if number is specified a counter will be displayed
     */
	statusIconProps?: number;

    /**
     * The callback sent must be called for the component to render.
     * The callback is how Timeline determines where to order the current snapshot with the rest of them.
     */
	initializeSnapshot: InitializeSnapshot;

	className?: string;
}

export interface IStatusIconProps {
    /**
     * Icon name that will be displayed to the left of the snapshot.
     * Ensure that css responsible for loading the icon is loaded with the component.
     */
	iconName: string;
    /**
     * Class name for the icon, can be used to render different colors
     */
	className?: string;
    /**
     * The type of the icon.
     * This determines what font-family to load
     */
	type?: VssIconType;
}


export interface ITimelineIconProps {
    /**
     * Icon name that will be displayed to the left of the snapshot.
     * Ensure that css responsible for loading the icon is loaded with the component.
     */
	name: string;
    /**
     * The type of the icon.
     * This determines what font-family to load
     */
	type: VssIconType;
}

export type InitializeSnapshot = (resource: any, callback: SnapshotCallBack) => void;
export type SnapshotCallBack = (marker: Date) => void;

Components.add("buildTimelineDeploymentsView", SnapshotSample);